import { Backdrop } from '@mui/material';
import { Box } from '@mui/system';
import UPButton from 'components/button';
import ListboxWithCheckboxes from 'components/listbox-with-checkbox/ListBoxWithCheckbox';
import { toNumber } from 'lodash';
import { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  clearEmployeeFunctionLinkErrorsAction,
  linkFunctionsToEmployeesAction,
  replaceLinkedFunctionsAction,
} from 'store/actions/employee-function-link-actions';
import {
  addEmployeeFunctionLinkErrorSelector,
  addEmployeeFunctionLinkSuccessSelector,
  updateEmployeeFunctionLinkErrorSelector,
  updateEmployeeFunctionLinkSuccessSelector,
} from 'store/selectors/employee-function-link-selectors';
import theme from 'theme';
import {
  EMPLOYEE_FUNCTION_LINKS__ADD_TITLE,
  EMPLOYEE_FUNCTION_LINKS__EDIT_TITLE,
  EMPLOYEE_FUNCTION_LINKS__EMPLOYEES_LABEL,
  EMPLOYEE_FUNCTION_LINKS__EMPLOYEES_REQUIRED,
  EMPLOYEE_FUNCTION_LINKS__FUNCTIONS_LABEL,
  EMPLOYEE_FUNCTION_LINKS__FUNCTIONS_REQUIRED,
  GENERAL__CANCEL_BUTTON,
  GENERAL__SAVE_BUTTON,
} from 'translations/constants';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/Close.svg';
import { ErrorText, FormRow, ModalCloseIcon, ModalHeader, ModalTitle, ModalWrapper } from '../motive-exceptions/styles';
import { ModalActions, ModalBody, ModalLayout } from './styles';
import { EmployeeFunctionLinkModalProps } from './types';

interface FormValues {
  employeeIds: Array<string | number>;
  functionIds: Array<string | number>;
}

const EmployeeFunctionLinkModal = (props: EmployeeFunctionLinkModalProps): JSX.Element => {
  const { onClose, linkToEdit, employeeOptions, functionOptions } = props;
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();

  const isEdit = !!linkToEdit;
  const addError = useAppSelector(addEmployeeFunctionLinkErrorSelector);
  const addSuccess = useAppSelector(addEmployeeFunctionLinkSuccessSelector);
  const updateError = useAppSelector(updateEmployeeFunctionLinkErrorSelector);
  const updateSuccess = useAppSelector(updateEmployeeFunctionLinkSuccessSelector);
  const submitError = isEdit ? updateError : addError;

  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: {
      employeeIds: linkToEdit ? [linkToEdit.employeeId] : [],
      functionIds: linkToEdit ? linkToEdit.functionIds : [],
    },
  });

  useEffect(() => {
    dispatch(clearEmployeeFunctionLinkErrorsAction());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit ? updateSuccess : addSuccess) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSuccess, updateSuccess]);

  const onSubmit = useCallback(
    (values: FormValues) => {
      const functionIds = values.functionIds.map(toNumber);
      if (isEdit) {
        dispatch(
          replaceLinkedFunctionsAction({
            companyId: toNumber(companyId),
            employeeId: linkToEdit.employeeId,
            functionIds,
          }),
        );
        return;
      }
      dispatch(
        linkFunctionsToEmployeesAction({
          companyId: toNumber(companyId),
          employeeIds: values.employeeIds.map(toNumber),
          functionIds,
        }),
      );
    },
    [companyId, dispatch, isEdit, linkToEdit],
  );

  return (
    <Backdrop open sx={{ zIndex: '1200' }}>
      <ModalWrapper open onClose={onClose}>
        <ModalLayout>
          <ModalHeader>
            <ModalTitle>
              {isEdit ? t(EMPLOYEE_FUNCTION_LINKS__EDIT_TITLE) : t(EMPLOYEE_FUNCTION_LINKS__ADD_TITLE)}
            </ModalTitle>
            <ModalCloseIcon onClick={onClose}>
              <CloseIcon fill={theme.palette.secondary.contrastText} />
            </ModalCloseIcon>
          </ModalHeader>

          <ModalBody>
            <FormRow>
              <Controller
                name="employeeIds"
                control={control}
                rules={{ validate: v => (Array.isArray(v) && v.length > 0) || 'required' }}
                render={({ field }) => (
                  <ListboxWithCheckboxes
                    label={t(EMPLOYEE_FUNCTION_LINKS__EMPLOYEES_LABEL)}
                    options={employeeOptions}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    required
                    searchable={!isEdit}
                    enableBulkActions={!isEdit}
                    maxListHeight={200}
                    helperText={t(EMPLOYEE_FUNCTION_LINKS__EMPLOYEES_REQUIRED)}
                  />
                )}
              />
            </FormRow>

            <FormRow>
              <Controller
                name="functionIds"
                control={control}
                rules={{ validate: v => (Array.isArray(v) && v.length > 0) || 'required' }}
                render={({ field }) => (
                  <ListboxWithCheckboxes
                    label={t(EMPLOYEE_FUNCTION_LINKS__FUNCTIONS_LABEL)}
                    options={functionOptions}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    required
                    searchable
                    enableBulkActions
                    maxListHeight={200}
                    helperText={t(EMPLOYEE_FUNCTION_LINKS__FUNCTIONS_REQUIRED)}
                  />
                )}
              />
            </FormRow>

            {submitError && (
              <FormRow>
                <ErrorText>{t(submitError)}</ErrorText>
              </FormRow>
            )}
          </ModalBody>

          <ModalActions>
            <Box>
              <UPButton text={t(GENERAL__SAVE_BUTTON)} onClick={handleSubmit(onSubmit)} />
            </Box>
            <Box>
              <UPButton text={t(GENERAL__CANCEL_BUTTON)} outlined onClick={onClose} />
            </Box>
          </ModalActions>
        </ModalLayout>
      </ModalWrapper>
    </Backdrop>
  );
};

export default EmployeeFunctionLinkModal;
